// 1. Imports React
// (Aucun import React nécessaire ici)

// 2. Imports externes
import {
  CalendarDays,
  Clock,
  LayoutDashboard,
  Timer,
  TrendingDown,
  TrendingUp,
  Trophy,
  Users,
} from "lucide-react";

// 3. Imports composants UI
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

// 4. Imports hooks personnalisés
import { useAuth } from "@/contexts/AuthContext";
import { useCompanyStats } from "@/hooks/useStats";
import { MonthlyDailyChart } from "@/components/charts/MonthlyDailyChart";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// 5. Composant
export const AdminDashboardPage = () => {
  // ========== A. Custom Hooks ==========
  const { user } = useAuth();
  const companyId = user?.companyId;
  const [selectedPeriod, setSelectedPeriod] = useState<string>("month");

  const {
    data: stats,
    isLoading: loading,
    error: error,
  } = useCompanyStats(companyId!, selectedPeriod);

  // ========== B. Données calculées ==========

  // Fonction pour formater le changement en pourcentage
  const formatChange = (change: number, comparisonLabel?: string): string => {
    const sign = change >= 0 ? "+" : "";
    const comparison = comparisonLabel || "au mois dernier";
    return `${sign}${change}% par rapport à ${comparison}`;
  };

  // Fonction pour obtenir l'icône selon le changement
  const getTrendIcon = (change: number | undefined) => {
    if (change === undefined) return TrendingUp; // Par défaut si pas encore calculé
    return change >= 0 ? TrendingUp : TrendingDown;
  };

  const statsCards = [
    {
      title: "Employés actifs",
      value: stats?.activeEmployees || 0,
      change: `${stats?.totalEmployees || 0} au total`,
      icon: Users,
      trendIcon: TrendingUp, // Pas de changement pour les employés totaux
      showTrend: false,
    },
    {
      title: "En poste actuellement",
      value: stats?.activeSessions || 0,
      change: `Pointages en cours`,
      icon: Clock,
      trendIcon: TrendingUp,
      showTrend: false,
    },
    {
      title: `Pointages ${stats?.periodLabel || "ce mois"}`,
      value: stats?.sessionsInPeriod || 0,
      change:
        stats?.sessionsChange !== undefined
          ? formatChange(stats.sessionsChange, stats.comparisonLabel)
          : "Calcul en cours...",
      icon: CalendarDays,
      trendIcon: getTrendIcon(stats?.sessionsChange),
      showTrend: stats?.sessionsChange !== undefined,
    },
    {
      title: `Heures ${stats?.periodLabel || "ce mois"}`,
      value: `${stats?.hoursInPeriod || 0}h`,
      change:
        stats?.hoursChange !== undefined
          ? formatChange(stats.hoursChange, stats.comparisonLabel)
          : stats?.averageDailyHours
          ? `${stats.averageDailyHours}h/jour`
          : "Calcul en cours...",
      icon: Timer,
      trendIcon: getTrendIcon(stats?.hoursChange),
      showTrend: stats?.hoursChange !== undefined,
    },
    
  ];

  // ========== C. Gestion des erreurs et états de chargement ==========
  // Afficher le skeleton pendant le chargement
  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="p-4 sm:p-6">
              <div className="space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-8 w-1/2" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Afficher l'erreur si la requête a échoué
  if (error) {
    return (
      <div className="flex items-center justify-center h-64 p-4 sm:p-6">
        <div className="text-center">
          <p className="text-red-600 mb-2 text-sm sm:text-base">Erreur</p>
          <p className="text-muted-foreground text-sm sm:text-base">
            {error.message}
          </p>
        </div>
      </div>
    );
  }

  // Afficher un message si aucune donnée n'est disponible
  if (!stats) {
    return (
      <div className="flex items-center justify-center h-64 p-4 sm:p-6">
        <p className="text-sm sm:text-base text-muted-foreground">
          Aucune donnée disponible
        </p>
      </div>
    );
  }

  // ========== D. JSX ==========
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
            <LayoutDashboard className="h-8 w-8 text-primary" />
            Dashboard
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Vue d'ensemble de votre entreprise
          </p>
        </div>
        <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Sélectionner une période" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Jour</SelectItem>
            <SelectItem value="week">Semaine</SelectItem>
            <SelectItem value="month">Mois</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Cartes statistiques */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {statsCards.map((stat, index) => (
          <Card key={index} className="p-4 sm:p-6">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
              <stat.icon className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground shrink-0" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold">{stat.value}</div>
              <div className="flex items-center gap-1 mt-1">
                {stat.showTrend && (
                  <stat.trendIcon
                    className={`h-3 w-3 shrink-0 ${
                      stat.trendIcon === TrendingDown
                        ? "text-red-600"
                        : "text-green-600"
                    }`}
                  />
                )}
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {stat.change}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Graphique des pointages - Pleine largeur pour accommoder tous les jours */}
      <Card className="p-3 sm:p-4 lg:p-6">
        <h2 className="mb-3 sm:mb-4 text-lg sm:text-xl font-semibold">
          Pointages {stats?.periodLabel || "ce mois"}
        </h2>
        <div className="w-full">
          <MonthlyDailyChart companyId={companyId} period={selectedPeriod} />
        </div>
      </Card>

      {/* Graphiques et informations */}
      <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
        {/* Top 3 meilleurs employés */}
        <Card className="p-4 sm:p-6">
          <h2 className="mb-4 text-lg sm:text-xl font-semibold">
            Top 3 employés {stats?.periodLabel || "du mois"}
          </h2>
          <div className="space-y-3">
            {stats.topPerformingEmployees &&
            stats.topPerformingEmployees.length > 0 ? (
              stats.topPerformingEmployees.map((employee, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 sm:gap-4 p-3 sm:p-4 bg-muted/50 rounded-lg"
                >
                  <div
                    className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shrink-0 ${
                      index === 0
                        ? "bg-yellow-100"
                        : index === 1
                        ? "bg-gray-100"
                        : "bg-orange-100"
                    }`}
                  >
                    {index === 0 ? (
                      <Trophy className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600" />
                    ) : index === 1 ? (
                      <Trophy className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
                    ) : (
                      <Trophy className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm sm:text-base truncate">
                      {employee.name}
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {employee.hours} heures {stats?.periodLabel || "ce mois"}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xl sm:text-2xl font-bold text-primary">
                      {employee.hours}h
                    </div>
                    <div className="text-xs text-muted-foreground hidden sm:block">
                      {stats?.periodLabel || "ce mois"}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex items-center gap-2 sm:gap-4 p-3 sm:p-4 bg-muted/50 rounded-lg">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <Trophy className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm sm:text-base">Aucun</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Aucune donnée disponible
                  </p>
                </div>
              </div>
            )}
          </div>
        </Card>
        {/* Sessions en cours */}
        <Card className="p-4 sm:p-6">
          <h2 className="mb-4 text-lg sm:text-xl font-semibold">
            Sessions en cours
          </h2>
          <div className="flex flex-col items-center justify-center mb-4">
            <div className="text-4xl sm:text-5xl font-bold text-primary mb-2">
              {stats?.activeSessions || 0}
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground text-center">
              Employés actuellement en session
            </p>
          </div>
          {/* Afficher la liste des employés */}
          {stats?.activeSessionsEmployees &&
          stats.activeSessionsEmployees.length > 0 ? (
            <div className="space-y-2 mt-4">
              {stats.activeSessionsEmployees.map((employee, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-muted/50 rounded-lg"
                >
                  <div className="w-2 h-2 rounded-full bg-green-500 shrink-0"></div>
                  <span className="text-xs sm:text-sm font-medium truncate">
                    {employee.name}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-xs sm:text-sm text-muted-foreground mt-4">
              Aucune session en cours
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
